import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import ABSSvgIcon from '@abs/core/ABSSvgIcon';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { enGB, de as deAT } from 'date-fns/locale';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const locales = { enGB, deAT };

const IMPORTANCE_META = {
  highest: { icon: 'heroicons-solid:chevron-double-up', color: '#ef4444', borderColor: '#ef4444' },
  higher: { icon: 'heroicons-solid:chevron-double-up', color: '#ef4444', borderColor: '#ef4444' },
  high: { icon: 'heroicons-solid:chevron-up', color: '#ef4444', borderColor: '#ef4444' },
  normal: null,
  low: { icon: 'heroicons-solid:chevron-down', color: '#6b7280', borderColor: '#6b7280' },
  lower: { icon: 'heroicons-solid:chevron-double-down', color: '#6b7280', borderColor: '#6b7280' },
  lowest: { icon: 'heroicons-solid:chevron-double-down', color: '#9ca3af', borderColor: '#9ca3af' },
};

// Maps module codes to their brand colour and square logo file
const MODULE_META = {
  tl: { color: '#366893', logo: 'tl_orig.svg' },
  abs: { color: '#313f4e', logo: 'BS-BM-DBlau.svg' },
  iv: { color: '#ea7600', logo: 'iv_orig.svg' },
  bb: { color: '#a20f3b', logo: 'bb_orig.svg' },
  eo: { color: '#006d33', logo: 'eo_orig.svg' },
  pm: { color: '#59287f', logo: 'pm_orig.svg' },
};

function NotificationCard(props) {
  const { item, className, removing, removeDelay = 0 } = props;
  const navigate = useNavigate();
  const dateFnsLocale = locales[useSelector(({ i18n }) => i18n.language)] || locales.enGB;

  const { t } = useTranslation('abslayouts');
  const meta = MODULE_META[item.module] || MODULE_META.abs;
  const importanceMeta = IMPORTANCE_META[item.importance] || null;
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (removing && !isLeaving) {
      const t = setTimeout(() => setIsLeaving(true), removeDelay);
      return () => clearTimeout(t);
    }
  }, [removing]);

  const handleClose = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setIsLeaving(true);
    setTimeout(() => { if (props.onClose) props.onClose(item.id); }, 300);
  };

  const handleMarkRead = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (props.onAcknowledge) props.onAcknowledge(item.id);
  };

  const handleAcknowledge = () => {
    if (!item.link) return;

    if (props.onAcknowledge) props.onAcknowledge(item.id);

    if (item.link.startsWith('http://') || item.link.startsWith('https://')) {
      window.open(item.link, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.link);
    }
  };

  return (
    <Card
      className={clsx(
        'flex items-center relative w-full rounded-12 p-16 min-h-64 shadow-sm',
        item.link && 'cursor-pointer',
        className
      )}
      elevation={0}
      sx={{
        borderLeftWidth: 6,
        borderLeftStyle: 'solid',
        borderLeftColor: item.read ? 'transparent' : meta.color,
        transform: 'scale(1)',
        transition: 'border-left-color 0.15s ease, transform 0.18s ease, opacity 0.28s ease',
        '&:hover': { transform: 'scale(1.015)' },
      }}
      style={isLeaving ? { transform: 'translateX(110%)', opacity: 0 } : undefined}
      onClick={handleAcknowledge}
    >
      {/* Module logo */}
      <img
        src={`assets/images/logos/${meta.logo}`}
        alt={item.module}
        className="shrink-0 w-28 h-28 mr-12 object-contain"
      />

      <div className="flex flex-col flex-auto overflow-hidden">
        {item.title && (
          <Typography className="font-semibold line-clamp-1 text-14">{item.title}</Typography>
        )}
        {item.description && (
          <div
            className="line-clamp-2 text-12 mt-2 opacity-80"
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        )}
        <div className="flex items-center gap-6 mt-6">
          <Typography className="text-11 leading-none opacity-60" color="text.secondary">
            {formatDistanceToNow(new Date(item.time), { addSuffix: true, locale: dateFnsLocale })}
          </Typography>
          {importanceMeta && (
            <Tooltip title={`${t('IMPORTANCE')}: ${t(`IMPORTANCE_${item.importance.toUpperCase()}`)}`} placement="right">
              <span className="flex items-center justify-center shrink-0">
                <ABSSvgIcon size={12} style={{ color: importanceMeta.color }}>
                  {importanceMeta.icon}
                </ABSSvgIcon>
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="top-0 right-0 absolute flex items-center">
        {!item.read && (
          <Tooltip title={t('MARK_AS_READ')} placement="left">
            <IconButton disableRipple className="p-6" size="small" onClick={handleMarkRead}>
              <ABSSvgIcon size={12} className="opacity-50">
                heroicons-solid:check
              </ABSSvgIcon>
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('DISMISS_NOTIFICATION')} placement="left">
          <IconButton disableRipple className="p-6" size="small" onClick={handleClose}>
            <ABSSvgIcon size={12} className="opacity-50">
              heroicons-outline:trash
            </ABSSvgIcon>
          </IconButton>
        </Tooltip>
      </div>


    </Card>
  );
}

export default NotificationCard;
